import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  Stack,
  Radio,
  RadioGroup,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  InputAdornment,
} from '@mui/material';
import { toCents, fromCents } from '../../moneyUtils';

// Import Material-UI icons (v4 compatible)
import {
  CardGiftcard,
  Add,
  Close,
  Edit,
  Delete,
} from '@material-ui/icons';

const CashbackTab = (props) => {
  const currency = props.currency;

   // Modal state
  const [openCashbackModal, setOpenCashbackModal] = useState(false);
  const [selectedCashbackForEdit, setSelectedCashbackForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cashbackToDelete, setCashbackToDelete] = useState(null);

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle success states
  useEffect(() => {
    if (props.isSuccess) {
      setSnackbarMessage('Cashback operation completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenCashbackModal(false);
      setSelectedCashbackForEdit(null);
      
      // Reset success state after showing notification
      setTimeout(() => {
        props.resetCashbackSuccess && props.resetCashbackSuccess();
      }, 100);
    }
  }, [props.isSuccess]);

  // Handle error states
  useEffect(() => {
    if (props.isError) {
      setSnackbarMessage(`Error: ${props.isError.message || 'Something went wrong'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetCashbackError && props.resetCashbackError();
      }, 100);
    }
  }, [props.isError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

   // Cashback state
  const [cashbackData, setCashbackData] = useState({
    name: "Cashback",
    period: "",
    period_granularity: "",
    enabled: true,
    returned_premiums_count: "",
    premium_percentage: "",
    fixed_premium_amount: "",
  });

  // State for cashback type selection
  const [cashbackType, setCashbackType] = useState("percentage"); // "percentage" or "fixed"

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Use cashback data from props if available
  const cashbackRules = props.cashback || [];
  console.log("Cashback rules from props:", cashbackRules);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!cashbackData.name) {
      newErrors.name = "Name is required";
    }

    // Period validation
    if (!cashbackData.period || cashbackData.period === "") {
      newErrors.period = "Period is required";
    } else if (parseInt(cashbackData.period) <= 0) {
      newErrors.period = "Period must be greater than 0";
    }

    // Period granularity validation
    if (!cashbackData.period_granularity) {
      newErrors.period_granularity = "Period granularity is required";
    }

    // Returned premiums count validation
    if (!cashbackData.returned_premiums_count || cashbackData.returned_premiums_count === "") {
      newErrors.returned_premiums_count = "Returned premiums count is required";
    } else if (parseInt(cashbackData.returned_premiums_count) <= 0) {
      newErrors.returned_premiums_count = "Returned premiums count must be greater than 0";
    }

    // Cashback value validation based on type
    if (cashbackType === "percentage") {
      if (!cashbackData.premium_percentage || cashbackData.premium_percentage === "") {
        newErrors.premium_percentage = "Premium percentage is required";
      } else if (parseFloat(cashbackData.premium_percentage) <= 0) {
        newErrors.premium_percentage = "Premium percentage must be greater than 0";
      } else if (parseFloat(cashbackData.premium_percentage) > 100) {
        newErrors.premium_percentage = "Premium percentage cannot exceed 100%";
      }
    } else if (cashbackType === "fixed") {
      if (!cashbackData.fixed_premium_amount || cashbackData.fixed_premium_amount === "") {
        newErrors.fixed_premium_amount = "Fixed premium amount is required";
      } else if (parseFloat(cashbackData.fixed_premium_amount) <= 0) {
        newErrors.fixed_premium_amount = "Fixed premium amount must be greater than 0";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName) => {
    return touched[fieldName] && Boolean(errors[fieldName]);
  };

    // Cashback handlers
  const handleCashbackInputChange = (field) => (event) => {
    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));

    let value = event.target.value;
    
    // For number fields, keep as string if empty, convert to number if filled
    if (event.target.type === 'number') {
      if (value === "") {
        value = "";
      } else {
        const numValue = parseFloat(value);
        value = isNaN(numValue) ? "" : numValue;
      }
    }
    
    // Keep empty strings as empty strings for consistent form handling
    setCashbackData({
      ...cashbackData,
      [field]: value,
    });

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCashbackStatusChange = (event) => {
    setCashbackData({
      ...cashbackData,
      enabled: event.target.checked,
    });
  };

  const handleCashbackTypeChange = (event) => {
    const newType = event.target.value;
    setCashbackType(newType);
    
    // Clear errors for cashback value fields when switching types
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.premium_percentage;
      delete newErrors.fixed_premium_amount;
      return newErrors;
    });
    
    // When creating a new rule (not editing), clear the opposite field
    // When editing, preserve both values so user can see what was there
    if (!selectedCashbackForEdit) {
      if (newType === "percentage") {
        setCashbackData(prev => ({
          ...prev,
          fixed_premium_amount: "",
        }));
      } else {
        setCashbackData(prev => ({
          ...prev,
          premium_percentage: "",
        }));
      }
    }
    // When editing, don't clear the values - let user switch between viewing both
  };

  const handleOpenCashbackModal = (cashbackRule = null) => {
    if (cashbackRule) {
      console.log("Opening edit modal with cashback rule:", cashbackRule);
      setSelectedCashbackForEdit(cashbackRule);
      setCashbackData({
        name: cashbackRule.name || "Cashback",
        period: cashbackRule.period ? cashbackRule.period.toString() : "",
        period_granularity: cashbackRule.period_granularity || "",
        enabled: cashbackRule.enabled !== undefined ? cashbackRule.enabled : true,
        returned_premiums_count: cashbackRule.returned_premiums_count ? cashbackRule.returned_premiums_count.toString() : "",
        premium_percentage: cashbackRule.premium_percentage !== null && cashbackRule.premium_percentage !== undefined ? cashbackRule.premium_percentage.toString() : "",
        fixed_premium_amount: fromCents(cashbackRule.fixed_premium_amount),
      });
      console.log("Set cashback data:", {
        premium_percentage: cashbackRule.premium_percentage !== null && cashbackRule.premium_percentage !== undefined ? cashbackRule.premium_percentage.toString() : "",
        fixed_premium_amount: fromCents(cashbackRule.fixed_premium_amount),
      });
      // Set the cashback type based on which field has a value
      // Check for non-empty and non-zero values more accurately
      const hasPercentage = cashbackRule.premium_percentage !== null && 
                           cashbackRule.premium_percentage !== undefined && 
                           cashbackRule.premium_percentage !== "" && 
                           parseFloat(cashbackRule.premium_percentage) >= 0;
      
      const hasFixedAmount = cashbackRule.fixed_premium_amount !== null && 
                            cashbackRule.fixed_premium_amount !== undefined && 
                            cashbackRule.fixed_premium_amount !== "" && 
                            parseFloat(cashbackRule.fixed_premium_amount) >= 0;

      if (hasPercentage && !hasFixedAmount) {
        setCashbackType("percentage");
        console.log("Setting type to percentage, value:", cashbackRule.premium_percentage);
      } else if (hasFixedAmount && !hasPercentage) {
        setCashbackType("fixed");
        console.log("Setting type to fixed, value:", cashbackRule.fixed_premium_amount);
      } else if (hasPercentage && hasFixedAmount) {
        // Both have values, prioritize percentage (or you could prioritize based on business logic)
        setCashbackType("percentage");
        console.log("Both values present, defaulting to percentage");
      } else {
        setCashbackType("percentage"); // default when neither has a meaningful value
        console.log("Setting type to percentage (default - no meaningful values)");
      }
    } else {
      setCashbackData({
        name: "Cashback",
        period: "",
        period_granularity: "",
        enabled: true,
        returned_premiums_count: "",
        premium_percentage: "",
        fixed_premium_amount: "",
      });
      setCashbackType("percentage"); // default for new rules
      setSelectedCashbackForEdit(null);
    }
    setOpenCashbackModal(true);
  };

  const handleCloseCashbackModal = () => {
    setOpenCashbackModal(false);
    setSelectedCashbackForEdit(null);
    setCashbackType("percentage");
    setCashbackData({
      name: "Cashback",
      period: "",
      period_granularity: "",
      enabled: true,
      returned_premiums_count: "",
      premium_percentage: "",
      fixed_premium_amount: "",
    });
    // Reset validation state
    setErrors({});
    setTouched({});
  };

  const handleAddCashbackProgram = () => {
    // Mark all fields as touched to show errors
    const allFields = ['name', 'period', 'period_granularity', 'returned_premiums_count'];
    if (cashbackType === 'percentage') {
      allFields.push('premium_percentage');
    } else {
      allFields.push('fixed_premium_amount');
    }
    
    const touchedState = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    // Validate form
    if (!validateForm()) {
      // Form has errors, don't submit
      return;
    }

    // Base payload with required fields
    const cashbackPayload = {
      name: cashbackData.name,
      period: parseInt(cashbackData.period),
      period_granularity: cashbackData.period_granularity,
      enabled: cashbackData.enabled,
      returned_premiums_count: parseInt(cashbackData.returned_premiums_count),
    };

    // Add only the selected cashback type field
    if (cashbackType === "percentage") {
      cashbackPayload.premium_percentage = parseFloat(cashbackData.premium_percentage);
      // Don't include fixed_premium_amount in payload
    } else if (cashbackType === "fixed") {
      cashbackPayload.fixed_premium_amount = toCents(cashbackData.fixed_premium_amount);
      // Don't include premium_percentage in payload
    }

    if (selectedCashbackForEdit) {
      // Edit existing program
      if (props.handleEditCashBack) {
        console.log("Editing Cashback Rule:", cashbackPayload);
        props.handleEditCashBack({ ...cashbackPayload, cashback_guid: selectedCashbackForEdit.guid });
      }
    } else {
      // Add new program
      if (props.handleCashBack) {
        console.log("Creating Cashback Rule:", cashbackPayload);
        props.handleCashBack(cashbackPayload);
      }
    }
    
    handleCloseCashbackModal();
  };

  const handleDeleteCashback = (cashback_guid) => {
    if (props.handleDeleteCashBack) {
      console.log("Deleting Cashback Rule:", cashback_guid);
      props.handleDeleteCashBack(cashback_guid);
    }
  };

  const handleDeleteClick = (cashback) => {
    setCashbackToDelete(cashback);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (cashbackToDelete) {
      handleDeleteCashback(cashbackToDelete.guid);
      setDeleteConfirmOpen(false);
      setCashbackToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCashbackToDelete(null);
  };

  const hasCashbackPrograms = cashbackRules && cashbackRules.length > 0;
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Cashback Programs Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure loyalty rewards for claim-free periods
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenCashbackModal()}
            sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": {
              backgroundColor: "#fba82c",
            },
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
          >
            Add Cashback Rule
          </Button>
        </Stack>
      </Box>

      {hasCashbackPrograms ? (
        <Box>
          <Stack spacing={3}>
            {cashbackRules.map((program, index) => (
              <Card 
                key={program.guid || index} 
                sx={{ 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.0.08)",
                    borderColor: "#C9C9C9",
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F8F9FA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <CardGiftcard sx={{ fontSize: 20, color: "#173E4B" }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {program.premium_percentage
                            ? `${program.premium_percentage}% Cashback`
                            : program.fixed_premium_amount
                              ? `${currency ? currency + ' ' : ''}${fromCents(program.fixed_premium_amount)} Fixed Cashback`
                              : 'Cashback Rule'
                          }
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: program.enabled ? "#dcfce7" : "#fee2e2",
                            color: program.enabled ? "#166534" : "#991b1b",
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            display: "inline-block",
                          }}
                        >
                          {program.enabled ? "Active" : "Inactive"}
                        </Box>
                      </Box>
                    </Box>
                     <Box sx={{ display: "flex", gap: 1, mr: 1 }} onClick={(e) => e.stopPropagation()}>
                         <IconButton 
                           size="small" 
                           sx={{ color: "#6b7280" }}
                          onClick={() => handleOpenCashbackModal(program)}
                        title="Edit cashback rule"
                           >
                           <Edit sx={{ fontSize: 16 }} />
                         </IconButton>
                         <IconButton 
                           size="small" 
                           sx={{ color: "#ef4444" }}
                          onClick={() => handleDeleteClick(program)}
                        title="Delete cashback rule"
                         >
                           <Delete sx={{ fontSize: 16 }} />
                         </IconButton>
                       </Box>
                  
                  </Box>

                  {/* Cashback Details */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2, mb: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Period
                        </Typography>
                        <Typography variant="body2">
                          {program.period} {program.period_granularity}{program.period > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Returned Premiums Count
                        </Typography>
                        <Typography variant="body2">
                          {program.returned_premiums_count || 'Not set'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Premium Percentage
                        </Typography>
                        <Typography variant="body2">
                          {program.premium_percentage ? `${program.premium_percentage}%` : 'Not set'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Fixed Amount
                        </Typography>
                        <Typography variant="body2">
                          {program.fixed_premium_amount ? `${currency ? currency + ' ' : ''}${fromCents(program.fixed_premium_amount)}` : 'Not set'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Description */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: "block" }}>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Eligible after {program.period} {program.period_granularity}{program.period > 1 ? 's' : ''} with no claims
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px dashed #d1d5db",
            borderRadius: 2,
            p: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <CardGiftcard sx={{ fontSize: 24, color: "#6b7280" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No cashback rules yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Get started by creating your first cashback rule.<br />
            Cashback rules reward customers for claim-free periods.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleOpenCashbackModal()}
            sx={{
              textTransform: "none",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              px: 3,
              py: 1,
            }}
          >
            Create Cashback Rule
          </Button>
        </Box>
      )}

      {/* Cashback Creation/Edit Modal */}
      <Dialog
        open={openCashbackModal}
        onClose={handleCloseCashbackModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedCashbackForEdit ? "Edit Cashback Rule" : "Create Cashback Rule"}
            </Typography>
            <IconButton onClick={handleCloseCashbackModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          {/* Create Cashback Program Form */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
              * Required fields.
            </Typography>
            
            {/* Name Field */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Name *
              </Typography>
              <FormControl fullWidth error={hasFieldError("name")}>
                <Select
                  value={cashbackData.name}
                  onChange={handleCashbackInputChange("name")}
                  sx={{
                    backgroundColor: "#fafafa",
                  }}
                >
                  <MenuItem value="Cashback">Cashback</MenuItem>
                </Select>
                {getFieldError("name") && (
                  <FormHelperText>{getFieldError("name")}</FormHelperText>
                )}
              </FormControl>
            </Box>
            
            {/* Period and Period Granularity Row */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Period *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={cashbackData.period}
                  onChange={handleCashbackInputChange("period")}
                  variant="outlined"
                  error={hasFieldError("period")}
                  helperText={getFieldError("period")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Period Granularity *
                </Typography>
                <FormControl fullWidth error={hasFieldError("period_granularity")}>
                  <Select
                    value={cashbackData.period_granularity}
                    onChange={handleCashbackInputChange("period_granularity")}
                    sx={{
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <MenuItem value="Days">Day</MenuItem>
                    <MenuItem value="Week">Week</MenuItem>
                    <MenuItem value="Month">Month</MenuItem>
                    <MenuItem value="Year">Year</MenuItem>
                  </Select>
                  {getFieldError("period_granularity") && (
                    <FormHelperText>{getFieldError("period_granularity")}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Returned Premiums Count */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Returned Premiums Count *
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={cashbackData.returned_premiums_count}
                onChange={handleCashbackInputChange("returned_premiums_count")}
                variant="outlined"
                error={hasFieldError("returned_premiums_count")}
                helperText={getFieldError("returned_premiums_count") || "Number of premiums to return as cashback"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fafafa",
                  },
                }}
              />
            </Box>

            {/* Cashback Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                Cashback Type *
              </Typography>
              <FormControl>
                <RadioGroup
                  value={cashbackType}
                  onChange={handleCashbackTypeChange}
                  sx={{ flexDirection: "row" }}
                >
                  <FormControlLabel
                    value="percentage"
                    control={<Radio sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }} />}
                    label="Percentage of Premium"
                  />
                  <FormControlLabel
                    value="fixed"
                    control={<Radio sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }} />}
                    label="Fixed Amount"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Cashback Value Input - Conditional based on type */}
            {cashbackType === "percentage" ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Premium Percentage (%) *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="0"
                  value={cashbackData.premium_percentage || ""}
                  onChange={handleCashbackInputChange("premium_percentage")}
                  variant="outlined"
                  error={hasFieldError("premium_percentage")}
                  helperText={getFieldError("premium_percentage") || "Enter value between 0-100"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Fixed Premium Amount{currency ? ' (' + currency + ')' : ''} *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="0.00"
                  value={cashbackData.fixed_premium_amount || ""}
                  onChange={handleCashbackInputChange("fixed_premium_amount")}
                  variant="outlined"
                  error={hasFieldError("fixed_premium_amount")}
                  helperText={getFieldError("fixed_premium_amount") || "Enter fixed premium amount"}
                  InputProps={currency ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>{currency}</Typography>
                      </InputAdornment>
                    )
                  } : {}}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                    },
                  }}
                />
              </Box>
            )}

            {/* Program Status Toggle */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 2,
                backgroundColor: "#f9fafb",
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Program Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cashbackData.enabled ? "Active" : "Inactive"}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cashbackData.enabled}
                    onChange={handleCashbackStatusChange}
                    sx={{
                      color: "#173E4B",
                      "&.Mui-checked": {
                        color: "#173E4B",
                      },
                    }}
                  />
                }
                label=""
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseCashbackModal}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCashbackProgram}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#fba92c",
              "&:hover": {
                backgroundColor: "#f59e0b",
              }
            }}
          >
            {selectedCashbackForEdit ? "Update Rule" : "Create Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Cashback Rule
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the cashback rule "{cashbackToDelete && (
              cashbackToDelete.premium_percentage
                ? `${cashbackToDelete.premium_percentage}% Cashback`
                : cashbackToDelete.fixed_premium_amount
                  ? `${currency ? currency + ' ' : ''}${fromCents(cashbackToDelete.fixed_premium_amount)} Fixed Cashback`
                  : 'Cashback Rule'
            )}"?
            <br/><br/>
            <strong>Details:</strong>
            {cashbackToDelete && (
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Period: {cashbackToDelete.period} {cashbackToDelete.period_granularity}{cashbackToDelete.period > 1 ? 's' : ''}</li>
                <li>Returned Premiums: {cashbackToDelete.returned_premiums_count}</li>
                <li>Status: {cashbackToDelete.enabled ? 'Active' : 'Inactive'}</li>
              </ul>
            )}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Processing cashback operation...
          </Typography>
        </Box>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CashbackTab;